import { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "@/lib/api";

const DEFAULTS = {
  announcement: { enabled: false, text: "", link: "", link_label: "Learn more" },
  hero: {
    overline: "The North American Hunting Hub",
    headline: "WELCOME\nHOME.",
    subheadline: "Your next hunt starts here. Plan it, build your gear, find trusted hunting services, understand the rules, and keep the story.",
    cta_label: "Plan a Hunt",
    background_image: "https://images.unsplash.com/photo-1612441804231-77a36b284856?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDN8MHwxfHNlYXJjaHwyfHxydWdnZWQlMjBtb3VudGFpbiUyMGxhbmRzY2FwZSUyMHN1bnJpc2UlMjBkcmFtYXRpYyUyMGxpZ2h0fGVufDB8fHx8MTc4NjcxNjU0M3ww&ixlib=rb-4.1.0&q=85&w=1600",
  },
  footer: { tagline: "Everything for the hunt. All in one place.", motto: "Plan. Gear up. Hunt. Remember." },
  goods: { enabled: true, eyebrow: "HOMERANGE GOODS", headline: "Made for wherever you call home.", description: "The first HomeRange collection. Built for the field, camp, and everything in between.", button_text: "SHOP HOMERANGE" },
  nav: [],
  footer_links: [],
  sections: [],
  featured: { enabled: false, title: "Featured on HomeRange", hunts: [], businesses: [], products: [] },
};

const merge = (d) => ({
  ...DEFAULTS,
  ...d,
  hero: { ...DEFAULTS.hero, ...(d.hero || {}) },
  footer: { ...DEFAULTS.footer, ...(d.footer || {}) },
  goods: { ...DEFAULTS.goods, ...(d.goods || {}) },
  announcement: { ...DEFAULTS.announcement, ...(d.announcement || {}) },
  featured: { ...DEFAULTS.featured, ...(d.featured || {}) },
  nav: Array.isArray(d.nav) ? d.nav : DEFAULTS.nav,
  footer_links: Array.isArray(d.footer_links) ? d.footer_links : DEFAULTS.footer_links,
});

const SiteContentContext = createContext({ content: DEFAULTS, refetch: () => {} });
export const useSiteContent = () => useContext(SiteContentContext).content;
export const useSiteContentRefetch = () => useContext(SiteContentContext).refetch;

export function SiteContentProvider({ children }) {
  const [content, setContent] = useState(DEFAULTS);
  const lastFetch = useRef(0);
  const refetch = useCallback((force = false) => {
    const now = Date.now();
    if (!force && now - lastFetch.current < 2000) return; // guard against focus spam
    lastFetch.current = now;
    api.siteContent().then((d) => { if (d) setContent(merge(d)); }).catch(() => {});
  }, []);

  useEffect(() => {
    refetch(true);
    // Live-refresh: pick up admin edits when returning to the tab (no hard reload).
    const onFocus = () => refetch();
    const onVisible = () => { if (document.visibilityState === "visible") refetch(); };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refetch]);

  return <SiteContentContext.Provider value={{ content, refetch }}>{children}</SiteContentContext.Provider>;
}
